// External
import React, { Component } from "react";
import { connect } from "react-redux";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import Moment from "moment";
import { cloneDeep, debounce, get as _get, isNull, startCase } from "lodash";

// Internal
import DraggableTurfAssignment from "../cards/DraggableTurfAssignment";
import DraggableShiftCanvasser from "../cards/DraggableShiftCanvasser";
import ColorCheckbox from "../theme/forms/ColorCheckbox";
import FilterDisplay from "../subscreens/FilterDisplay";
import NoteTagFilter from "../theme/forms/NoteTagFilter";
import NoteTagList from "../theme/buttons/NoteTag";
import {
  filterAndSortItems,
  filterItemsByNoteTag,
  handleSearchChange,
  sortItems,
} from "../../utils/FilterDisplayUtils";
import CanvasserLegend from "../theme/modals/CanvasserLegend";
import {
  findCanvassersAssignedToShift,
  findCheckedInCanvassers,
} from "../../utils/schedulerUtils";

import api from "../../store/api/api";
import {
  createShift,
  createShiftCanvasser,
  getSchedule,
  pullCampaignCanvassers,
  pullCampaignDoorCanvassPasses,
  pullSiteNoteCategories,
  updateShiftCanvasser,
} from "../../store/api/calls";

import * as doorCalls from "../../store/api/calls/doors";
import { updateDoorCrewCanvasser } from "../../store/api/calls/doors";

import {
  setCrews,
  setCrewsShiftData,
  setSessionVar,
} from "../../store/actions";

import NavBaseTemplate from "../templates/NavBaseTemplate";
import FeedbackTile from "../theme/modals/FeedbackTile";
import Tabs from "../theme/menus/Tabs";
import Input from "../theme/forms/Input";
import BaseModal from "../theme/modals/BaseModal";
import FromToDatePickerInput from "../theme/forms/FromToDatePickerInput";
import TurfDropoffMapModal from "../maps/TurfDropoffMapModal";
import TurfNoteModal from "../theme/modals/TurfNoteModal";
import { withRouter } from "../../utils/withRouter";

class DoorSchedule extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      pageError: false,
      errMsg: "There was a server error",
      turfs: [],
      crews: [],
      crewsDrop: [],
      displayedCanvassers: [],
      selectedDate: new Moment(Date.now()).format("YYYY-MM-DD"),
      sideBarSelection: "turfs",
      showLegend: false,
      showAssignedCanvassers: true,
      crewDropMatActive: false,
      //canvasser filters
      availableCanvasserStatuses: [
        "checked_in",
        "not_checked_in",
        "unscheduled",
      ],
      displayedCanvasserStatuses: [
        "checked_in",
        "not_checked_in",
        "unscheduled",
      ],
      //turf filters
      turfStatusFilters: ["walked"],
      turfPassFilter: 0,
      showTurfFilters: false,
      intendedWalkStartDate: null,
      intendedWalkEndDate: null,
      turfCodeFilter: "walkable",
      canvasserFilters: {
        id: "canvassers",
        keyword: "",
        keywordPersist: "",
        noteTags: [],
        sortBy: "lastname",
        sortDsc: false,
        searchFields: ["firstname", "lastname", "created_at"],
        sortFields: [
          {
            display: "Name",
            value: "lastname",
          },
          {
            display: "Creation Date",
            value: "created_at",
          },
          {
            display: "Status",
            value: "status",
          },
        ],
      },

      turfFilters: {
        id: "turfs",
        keyword: "",
        keywordPersist: "",
        noteTags: [],
        sortBy: "name",
        sortDsc: false,
        searchFields: [],
        sortFields: [
          {
            display: "Name",
            value: "name",
          },
          {
            display: "Doors",
            value: "household_count",
          },
          {
            display: "Pass",
            value: "doorcanvasspass",
          },
        ],
      },

      // shifts
      shiftData: [],
      showShiftModal: false,
      shiftStart: undefined,
      shiftEnd: undefined,
      invalidInput: false,
      invalidTime: false,
      shiftError: false,
      // crews
      crewsheet_modal_show: false,
      crewsheet_crews: [],
      crewsheet_get_dirs: true,
      crewsheet_selected_shift_idx: 0,

      //dropoff modal
      dropoff_modal_crew: null,
      focusedOffice: 0,
    };

    this.focusedDoorCanvass = 0;

    this.colorClassMapping = {
      checked_in: { class: "_green", color: "#33a02c" },
      not_checked_in: { class: "_red", color: "#e31a1c" },
      unscheduled: { class: "_grey", color: "darkgrey" },
      assigned: { class: "_blue", color: "#1f78b4" },
    };

    this.turfClassMapping = {
      // checked_in: {class: '_green', color: "#33a02c"},
      walked: { class: "_blue", color: "#1f78b4" },
      assigned: { class: "_red", color: "#e31a1c" },
      unwalked: { class: "_green", color: "#33a02c" },
    };

    this.dateChangeTimeout = undefined;
  }

  componentDidMount() {
    pullSiteNoteCategories();
    if (this.props.focusedCampaign !== 0 && !this.props.focusedCampaign.id) {
      setTimeout(() => this.componentDidMount(), 200);
    } else {
      this.getDataOnLoad();
    }
  }

  componentWillUnmount() {
    clearTimeout(this.dateChangeTimeout);
  }

  /*
   * Data currently comes in sets of shifts. We're not using that yet, so for the moment we just pull the list of assignedSites,
   * merge them in with the assignedSites object, and then we can show who is available.
   */
  getAndParseSchedule = () => {
    this.setState({ loading: true });
    return getSchedule(this.state.focusedOffice, this.state.selectedDate)
      .then((data) => {
        let scs = [];
        let scsIds = [];
        data.forEach((d) => {
          d.canvassers.forEach((shift_canvasser) => {
            let c = this.props.canvassers.find(
              (item) => item.id === shift_canvasser.canvasser
            );

            if (c && !scsIds.includes(c.id)) {
              c.shift = shift_canvasser.shift;
              c.present = shift_canvasser.present;
              c.assigned =
                !isNull(shift_canvasser.crew) ||
                !isNull(shift_canvasser.doorcrew);
              c.scheduled = shift_canvasser.scheduled;
              scs.push(c);
              scsIds.push(c.id);
            }
          });
        });

        let scheduled_ids = scs.length ? scs.map((_d) => _d.id) : [];
        let final_canvassers = [
          ...scs,
          ...this.props.canvassers.filter((c) => !scheduled_ids.includes(c.id)),
        ];

        final_canvassers = final_canvassers.length
          ? final_canvassers.map(this.setShiftCanvasserStatus)
          : [];

        final_canvassers = final_canvassers.filter(
          (n) => +n.office === this.state.focusedOffice
        );

        this.setState({
          displayedCanvassers: final_canvassers,
          shiftData: data,
        });
        if (data.length) {
          this.props.setCrewsShiftData(data[0]);
        }
      })
      .finally(() => this.setState({ loading: false }));
  };

  setShiftCanvasserStatus = (d) => {
    if (+d.shift !== this.props.shift_data.id) {
      d.status = "unscheduled";
    } else if (d.scheduled && d.present) {
      d.status = "checked_in";
    } else if (d.scheduled && !d.present) {
      d.status = "not_checked_in";
    } else {
      d.status = "unscheduled";
    }

    //loop over crew and find out if the id is assigned
    let crewmembers = this.state.crews.reduce((acc, item, idx) => {
      let members = item.seats
        .filter((s) => !!s.canvasser)
        .map((s) => s.canvasser);
      acc = [...acc, ...members];
      return acc;
    }, []);

    if (crewmembers.includes(d.id)) {
      d.assigned = true;
    }
    return d;
  };

  getDataOnLoad() {
    this.setState({ loading: true });
    const {params} = this.props;

    this.focusedDoorCanvass = params.doorcanvass_id || 0;
    let fullDoorCanvass = this.props.doorCanvasses.find(
      (d) => d.id === this.focusedDoorCanvass
    );
    if (fullDoorCanvass)
      this.setState({ focusedOffice: +fullDoorCanvass.office });

    // Site canvass id will come from url params
    new Promise((resolve, reject) => {
      if (this.state.focusedOffice) {
        resolve();
      } else {
        return api
          .get(`/doorcanvasses/${this.focusedDoorCanvass}/`)
          .then((res) => {
            this.setState({ focusedOffice: +res.data.office });
            return resolve();
          })
          .catch((err) => {
            console.error(err);
            this.setState({
              pageError: true,
              errMsg: "Error loading Site Canvass Data",
              loading: false,
            });

            this.setState({ focusedOffice: 0 });
            return reject();
          });
      }
    })
      .then(() => {
        Promise.all([
          pullCampaignDoorCanvassPasses(),
          pullCampaignCanvassers({
            // office: this.state.focusedOffice,
            abilities: 1,
          }),
          doorCalls.pullDoorCrews({
            canvass: this.focusedDoorCanvass,
            date: this.state.selectedDate,
          }),
          api
            .get("/turfs/", { canvass: this.focusedDoorCanvass, notes: 1 })
            .then((res) => res.data),
          doorCalls.getTurfCodes(),
        ])
          .then((data) => {
            return this.getAndParseSchedule().then((res) => {
              // crews correspond to the 4th call in the promiseAll above.
              let turfs = (data[3] || [])
                .filter((turf) => {
                  // hide locked turfs, as they shouldn't be schedulable
                  return !(
                    this.props.doorCanvassPasses.find(
                      (p) => p.id === turf.doorcanvasspass
                    ) || {}
                  ).is_locked;
                })
                .sort((a, b) => a.name.localeCompare(b.name)) //sort by name
                .map((d) => ({
                  ...d,
                  passname: (
                    this.props.doorCanvassPasses.find(
                      (p) => p.id === d.doorcanvasspass
                    ) || {}
                  ).name,
                })); //get the name of the turf's pass
              let crews = this.props.crews.map((crew) =>
                this.addTurfsToSeats(crew, turfs)
              );
              let crewsheet_crews = crews.map((d) => d.id);
              const deepclone_crew = cloneDeep(
                this.props.crews.sort((a, b) => (a.id < b.id ? 1 : -1))
              );
              this.setState({
                turfs,
                crews: deepclone_crew,
                crewsheet_crews,
                loading: false,
              });
            });
          })
          // if we have a shift, then we need to make sure that it is properly set
          .then(() => {
            if (this.state.shiftData.length) {
              this.props.setCrewsShiftData(this.state.shiftData[0]);
              this.setState({ crewsheet_selected_shift_idx: 0 });
            }
          })
          .catch((err) => {
            // Promise.all catch
            console.error(err);
            this.setState({
              loading: false,
              pageError: true,
              errMsg: "Error retrieving crew data.",
            });
          });
      })
      .catch((err) => {
        console.error(err);
        this.setState({ loading: false, pageError: true });
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  }

  //THIS FUNCTION HANDLES THE DROPPING OF A SITEROUTE INTO SCHEDULE TO CREATE A CREW
  onDragEnd = (result) => {
    const { destination, source, draggableId } = result;
    document.getElementById("crew-drop-mat").className = "crew-drop-mat inline";
    this.makeAddSeatDropVisible(false);
    // destination will be null when dropped outside of context or it ended up in same column and same index
    if (
      !destination || //we don't know where to put it
      (destination.droppableId === source.droppableId &&
        destination.index === source.index) || //it didn't go anywhere.
      this.state.shiftData.length === 0 //there are no shifts to assign it to
    ) {
      this.setState({ crewDropMatActive: false });
      return;
    }

    const drop_is_seat_assignment =
      destination.droppableId.includes("turf-assign") &&
      source.droppableId === "displayedCanvassers";

    const drop_is_turf_reassignment =
      destination.droppableId.includes("turf-assign") &&
      source.droppableId === "turfs";

    const drop_is_seat_creation =
      destination.droppableId.includes("create-seat");

    // now need to reorder.
    const start = this.state[source.droppableId];

    let endId;
    if (destination.droppableId === "crewsDrop" || drop_is_seat_assignment) {
      endId = "crews";
    } else {
      endId = destination.droppableId;
    }

    let end = this.state[endId];

    if (start === end) {
      const newColumnIds = Array.from(start);
      let spliced = newColumnIds.splice(source.index, 1);
      if (spliced.length > 0) {
        newColumnIds.splice(destination.index, 0, spliced[0]);
      }

      const newState = {
        ...this.state,
        crewDropMatActive: false,
        [destination.droppableId]: newColumnIds,
      };

      this.setState(newState);
      return;
    }

    //handle canvasser seat assignment
    if (drop_is_seat_assignment) {
      let target_canvasser_id = draggableId.split("_")[1];
      let target_crewcanvasser_id =
        destination.droppableId.match(/_seat_(\d+)/)[1];
      this._handleCanvasserSelect(target_crewcanvasser_id, target_canvasser_id);
      return;
    }

    if (drop_is_seat_creation) {
      let turf_id = Number(draggableId.split("_")[1]);
      let crew_id = Number(destination.droppableId.split("_")[1]);
      this.createDoorCrewCanvasser(crew_id, turf_id);
      return;
    }

    if (drop_is_turf_reassignment) {
      return;
    }

    // move from one list to another. This is used for moving turfs to scheduled crews.
    const newStart = Array.from(start);
    const newFinish = Array.from(end);

    let item = newStart.slice(source.index, source.index + 1);

    if (item.length > 0) {
      newFinish.splice(destination.index, 0, { ...item[0] });
    }

    const newState = {
      ...this.state,
      crewDropMatActive: false,
      [source.droppableId]: start,
      [endId]: newFinish,
    };

    //last possibility is that we need to create a crew
    if (
      source.droppableId === "turfs" &&
      this.state.sideBarSelection === "turfs"
    ) {
      let shift_id = this.state.shiftData[
        this.state.crewsheet_selected_shift_idx
      ]
        ? this.state.shiftData[this.state.crewsheet_selected_shift_idx].id
        : false;
      let turf_id = Number(draggableId.split("_")[1]);

      if (!shift_id) {
        // Shifts must exist by this point and if they don't something is wrong.
        return;
      }

      // Need to do api call to preserve new crew and then call setState in then
      let requestData = {
        canvass: this.focusedDoorCanvass,
        shift: shift_id,
        seats: [{ turf: turf_id }],
      };

      this.setState({ loading: true });

      doorCalls
        .createDoorCrew(requestData)
        .then((crew) => {
          crew = this.addTurfsToSeats(crew);
          newState.loading = false;
          newState.turfs = newState.turfs.map((d) =>
            d.id === turf_id ? { ...d, assigned: 1 } : d
          );
          this.setState(newState);
          this.setCrewsFromProps();
          return crew;
        })
        .then((crew) => {
          // HACK: Seats weren't showing up because of a race condition
          // that probably exists in the api where crew_id pks aren't valid by the time
          // the serializer calls them
          !crew.seats.length && this.createDoorCrewCanvasser(crew.id, turf_id);
        });
    } else {
      this.setState(newState);
    }
  };

  addTurfsToSeats = (crew, turfs) => {
    if (!turfs) turfs = this.state.turfs;
    crew.seats.forEach((seat) => {
      let turf_id = typeof seat.turf == "object" ? seat.turf.id : seat.turf; //on incoming this can be an id. on update, this is an object
      let turf = turfs.find((d) => d.id === turf_id);
      seat.turf = turf;
    });

    return crew;
  };

  setCrewsFromProps = () => {
    let crews = this.props.crews
      .sort((a, b) => (a.id < b.id ? 1 : -1))
      .map((crew) => this.addTurfsToSeats(crew));

    this.setState({
      crews,
    });
  };

  makeAddSeatDropVisible = (show = true) => {
    let elements = document.getElementsByClassName("add-seat-drop-mat");
    for (var i = 0; i < elements.length; i++) {
      if (show) {
        elements[i].className = "add-seat-drop-mat active";
      } else {
        elements[i].className = "add-seat-drop-mat";
      }
    }
  };

  createDoorCrewCanvasser(crew_id, turf_id, canvasser_id) {
    //reminder that this method returns a full a crew. We swap out crews as our units, not assignedSites/seats.
    //you only need to replace the turf_id with the turf object

    doorCalls
      .createDoorCrewCanvasser({
        crew: crew_id,
        turf: turf_id,
        canvasser: canvasser_id,
      })
      .then((newcrew) => {
        let crews = this.state.crews.map((d) =>
          d.id === newcrew.id
            ? this.addTurfsToSeats({ ...newcrew, crewcanvasser_id: d.id })
            : d
        );
        let turfs = this.state.turfs.map((d) =>
          d.id === turf_id ? { ...d, assigned: 1 } : d
        );
        this.setState({ crews, turfs });
      });
  }

  updateDoorCrewCanvasser = (seat_id, seat_changes) => {
    return updateDoorCrewCanvasser(seat_id, seat_changes).then((data) => {
      // the full and updated crew is returned.
      this.setCrewsFromProps();
    });
  };

  updateOrCreateShiftCanvasser(canvasser_id, crew_id) {
    // Search through shift data assignedSites to see if they already exist, and if so update, else create them.
    let exists = false;

    this.state.displayedCanvassers.length &&
      this.state.displayedCanvassers.forEach((canvasser) => {
        if (canvasser.id === +canvasser_id && canvasser.shift) {
          exists = true;
        }
      });

    const updateState = (shiftCanvData) => {
      let newCanvList = [...this.state.displayedCanvassers];
      newCanvList.forEach((d) => {
        if (
          d.id === +shiftCanvData.canvasser &&
          d.shift === +shiftCanvData.shift
        ) {
          d.assigned = !isNull(shiftCanvData.doorcrew);
        }
      });
      this.setState({ displayedCanvassers: newCanvList });
    };

    if (exists) {
      updateShiftCanvasser({
        canvasser: canvasser_id,
        shift: this.state.shiftData[this.state.crewsheet_selected_shift_idx].id,
        doorcrew: crew_id,
      }).then(updateState);
    } else {
      // create a shift canvasser so other sitecanvasses can see this person was scheduled.
      createShiftCanvasser({
        canvasser: canvasser_id,
        shift: this.state.shiftData[this.state.crewsheet_selected_shift_idx].id,
        doorcrew: crew_id,
        scheduled: false,
        present: false,
      }).then(updateState);
    }
  }

  handleTurfNoteCreateOrDestroy = (turf) => {
    let turfs = [...this.state.turfs];
    let crews = [...this.state.crews];
    let turf_idx = turfs.findIndex((d) => d.id === turf.id);
    let seat_idx;
    let crew_idx = crews.findIndex((d) => {
      seat_idx = d.seats.findIndex((e) => e.turf.id === turf.id);
      if (seat_idx > -1) {
        return true;
      } else {
        return false;
      }
    });
    if (turf_idx > -1) {
      this.props.setTurfNoteModalTurf(turf);
      turfs[turf_idx] = turf;
    }

    if (crew_idx > -1 && seat_idx > -1) {
      crews[crew_idx].seats[seat_idx].turf = turf;
    }
    this.setState({ turfs, crews });
  };

  // note, to create a crewcanvasser, you can leave that argument null
  _handleCanvasserSelect = (crewcanvasser_id, canvasser_id) => {
    //TODO: HERE, AFTER CANVASSER HAS BEEN UPDATED, DISPATCH SORTING AND DROPOFF ASSIGNMENT TASK TO BACKEND.
    //this backend would take the crew id, assign the dropoff point using straight line geometry, optimize points then set the dropofforder and the dropoff point in the table.
    crewcanvasser_id = +crewcanvasser_id;
    canvasser_id = canvasser_id !== null ? +canvasser_id : null;

    let { crews } = this.state;
    let crew_idx = crews.findIndex((d) =>
      d.seats.map((d) => d.id).includes(crewcanvasser_id)
    );
    let seat_prev = cloneDeep(
      crews[crew_idx].seats.find((d) => d.id === crewcanvasser_id)
    );
    this.setState({ loading: true });

    return doorCalls
      .updateDoorCrewCanvasser(crewcanvasser_id, {
        canvasser: canvasser_id,
      })
      .then((data) => {
        let seats = data.seats.map((d) => ({ ...d, crewcanvasser_id: d.id }));
        let seat = seats.find((d) => d.crewcanvasser_id === crewcanvasser_id);
        crews[crew_idx] = this.addTurfsToSeats(data);
        seat.already_assigned_to = seat_prev.canvasser;
        this.setState({ crews, loading: false });
        return seat;
      })
      .then((data) => {
        // remove the person if they were there already
        if (data.already_assigned_to) {
          this.updateOrCreateShiftCanvasser(data.already_assigned_to, null);
        }
        // update the canvasser so they are marked as assigned in thelist
        this.updateOrCreateShiftCanvasser(data.canvasser, data.crew);
      });
  };

  _handleDeleteCrew = (id) => {
    this.setState({ loading: true });
    // let crew_canvasser_id = this.state.crews.find(d => d.id === id).seats.map(d => d.id)

    let { turf_ids, crew_canvasser_ids } = this.state.crews.reduce(
      (r, d) => {
        if (d.id !== id) return r;
        d.seats.forEach((s) => {
          r.crew_canvasser_ids.push(s.canvasser);
          r.turf_ids.push(s.turf.id);
        });
        return r;
      },
      { turf_ids: [], crew_canvasser_ids: [] }
    );

    doorCalls.deleteDoorCrew(id).then(() => {
      //remove assigned flag from assignedSites in crew;
      let newCanvList = [...this.state.displayedCanvassers].map((d) => ({
        ...d,
        assigned: crew_canvasser_ids.includes(d.id) ? false : d.assigned,
      }));
      let newTurfList = this.state.turfs.map((d) => ({
        ...d,
        assigned: turf_ids.includes(d.id) ? false : d.assigned,
      }));

      this.setState({
        crews: this.state.crews.filter((d) => d.id !== id),
        displayedCanvassers: newCanvList,
        turfs: newTurfList,
        loading: false,
      });
    });
  };

  _handleDeleteSeat = (seat_id, turf_id, canvasser_id) => {
    this.setState({ loading: true });
    return doorCalls.deleteDoorCrewCanvasser(seat_id).then((crew) => {
      //door canvass calls return the fully modified crew
      // remove the shift canvasser's reference to the crew so other sitecanvasses know they are not scheduled
      let crews = this.state.crews.map((d) => (d.id === crew.id ? crew : d));
      let turfs = this.state.turfs.map((d) =>
        d.id === turf_id ? { ...d, assigned: 0 } : d
      );
      this.updateOrCreateShiftCanvasser(canvasser_id, null);
      this.setState({ loading: false, crews, turfs });
    });
  };

  _renderSidebarTurfs() {
    const debouncedTurfSearch = debounce((query) => {
      const stateChanges = handleSearchChange(query);
      this.setState(
        {
          ...stateChanges[0],
          turfFilters: {
            ...this.state.canvasserFilters,
            keyword: stateChanges[1],
          },
        },
        () => this._filterTurfs()
      );
    }, 300);

    return (
      <>
        <div className={"canvasser-legend"}>
          <div className="filter-text">
            {(!!this.state.turfFilterText ||
              !!this.state.turfPassFilter ||
              !!this.state.intendedWalkStartDate ||
              !!this.state.turfCodeFilter) && (
              <div>
                {/*<h6 style={{ marginBottom: 5 }}>Filtering By:</h6>*/}
                {!!this.state.turfFilterText && (
                  <div>Name: {this.state.turfFilterText}</div>
                )}
                {!!this.state.turfPassFilter && (
                  <div>
                    Pass:{" "}
                    {
                      (
                        this.props.doorCanvassPasses.find(
                          (d) => this.state.turfPassFilter === d.id
                        ) || {}
                      ).name
                    }
                  </div>
                )}
                {(this.state.intendedWalkStartDate ||
                  this.state.intendedWalkEndDate) && (
                  <div>
                    Dates:
                    {Moment(this.state.intendedWalkStartDate).format(
                      "M/D"
                    )} - {Moment(this.state.intendedWalkEndDate).format("M/D")}
                  </div>
                )}
                <div>
                  {!isNaN(
                    (
                      this.props.turfCodes.find(
                        (d) => this.state.turfCodeFilter === d.id
                      ) || {}
                    ).name
                  )
                    ? startCase(
                        (
                          this.props.turfCodes.find(
                            (d) => this.state.turfCodeFilter === d.id
                          ) || {}
                        ).name
                      )
                    : startCase(this.state.turfCodeFilter)}{" "}
                  Turfs
                </div>
              </div>
            )}

            <FilterDisplay
              openFilterModal={() => this.setState({ showTurfFilters: true })}
              filters={this.state.turfFilters}
              handleSearchChange={(val) => {
                this.setState({
                  turfFilters: {
                    ...this.state.turfFilters,
                    keywordPersist: "",
                  },
                });
                debouncedTurfSearch(val);
              }}
              handleSortUpdate={(val) => {
                this.setState(
                  {
                    turfFilters: {
                      ...this.state.turfFilters,
                      sortBy: val,
                    },
                  },
                  this._filterTurfs
                );
              }}
              handleSortDirectionChange={() =>
                this.setState({
                  turfFilters: {
                    ...this.state.turfFilters,
                    sortDsc: !this.state.turfFilters.sortDsc,
                  },
                })
              }
            />
          </div>
        </div>
        <Droppable
          droppableId="turfs"
          style={{ minHeight: "100px", minWidth: "10rem", width: "100%" }}
        >
          {(provided) => (
            <div
              ref={provided.innerRef}
              style={{ minHeight: "100px", minWidth: "10rem", width: "100%" }}
            >
              {this._filterTurfs().map((item, index) => {
                return (
                  <DraggableTurfAssignment
                    key={"turf-" + item.id}
                    index={index}
                    id={`turf-card-${item.id}`}
                    name={item.name}
                    draggableId={`turf-card_${item.id}`}
                    color={this._getSidebarTurfColor(item)}
                    availableRoute={true}
                    data={item}
                    handleMouseUp={() => {
                      document.getElementById(
                        "crew-drop-mat"
                      ).className = `crew-drop-mat`;
                      this.makeAddSeatDropVisible(false);
                    }}
                    beforeDrag={() => {
                      document.getElementById(
                        "crew-drop-mat"
                      ).className = `crew-drop-mat active ${
                        this.state.crews.filter((n) => {
                          return (
                            n.shift ===
                            this.state.shiftData[
                              this.state.crewsheet_selected_shift_idx
                            ].id
                          );
                        }).length
                          ? "inline"
                          : ""
                      }`;
                      this.makeAddSeatDropVisible(true);
                    }}
                  />
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </>
    );
  }

  _getSidebarTurfColor = (turf) => {
    if (turf.assigned) {
      return this.turfClassMapping["assigned"].color;
    } else {
      return +_get(turf, "agg_data.targets_attempted", 0)
        ? this.turfClassMapping["walked"].color
        : this.turfClassMapping["unwalked"].color;
    }
  };

  _addOrRemoveTurfStatusFilter = (filter_name) => {
    if (this.state.turfStatusFilters.includes(filter_name)) {
      this.setState({
        turfStatusFilters: this.state.turfStatusFilters.filter(
          (d) => d !== filter_name
        ),
      });
    } else {
      this.setState({
        turfStatusFilters: [...this.state.turfStatusFilters, filter_name],
      });
    }
  };

  _renderTurfFilterOptions() {
    let filters = (
      <div className={"turf-filters"}>
        <Input
          type={"select"}
          value={this.state.turfPassFilter}
          title="Pass"
          onChange={(val) => {
            this.setState({ turfPassFilter: +val });
          }}
          options={[
            { value: 0, display: "All Passes" },
            ...this.props.doorCanvassPasses
              .filter((d) => d.canvass?.id === +this.focusedDoorCanvass)
              .map((d) => ({ value: d.id, display: d.name })),
          ]}
        />
        <div className={"label-imitation"}>Walk Status</div>
        <div className={"flx space_between"}>
          <ColorCheckbox
            className={this.turfClassMapping["assigned"].class}
            handleChange={() => this._addOrRemoveTurfStatusFilter("assigned")}
            value={!this.state.turfStatusFilters.includes("assigned")}
            title={"Assigned"}
            style={{ transform: "scale(.85)" }}
          />

          <ColorCheckbox
            className={this.turfClassMapping["unwalked"].class}
            handleChange={() => this._addOrRemoveTurfStatusFilter("unwalked")}
            value={!this.state.turfStatusFilters.includes("unwalked")}
            title={"Not Walked"}
            style={{ transform: "scale(.85)" }}
          />

          <ColorCheckbox
            className={this.turfClassMapping["walked"].class}
            handleChange={() => this._addOrRemoveTurfStatusFilter("walked")}
            value={!this.state.turfStatusFilters.includes("walked")}
            title={"Walked"}
            style={{ transform: "scale(.85)" }}
          />
        </div>

        <Input
          type={"select"}
          value={this.state.turfCodeFilter}
          title="Turf Code"
          onChange={(val) => {
            this.setState({ turfCodeFilter: val });
          }}
          options={[
            { value: "walkable", display: "All Walkable" },
            { value: "not_walkable", display: "All Not Walkable" },
            { value: "all", display: "All" },
            ...this.props.turfCodes
              // .filter((d) => d.walkable)
              .map((d) => ({ value: d.id, display: d.name })),
          ]}
        />

        <div className={"label-imitation"}>Intended Walk Date</div>
        <div className={"flx"}>
          <FromToDatePickerInput
            handleDateChange={(from, to) => {
              this.setState({
                intendedWalkStartDate: from,
                intendedWalkEndDate: to,
              });
            }}
            from={this.state.intendedWalkStartDate}
            to={this.state.intendedWalkEndDate}
            minDate={new Date(2020, 0, 1)}
            maxDate={new Date(2100, 0, 1)}
          />
          <button
            className={"button button-clear"}
            onClick={() => {
              this.setState({
                intendedWalkStartDate: null,
                intendedWalkEndDate: null,
              });
            }}
          >
            Clear Selected Dates
          </button>
        </div>
        <div className={"label-imitation mt-2"}>Note Tags</div>
        <NoteTagFilter
          onSubmit={(res) => {
            this.setState({
              turfFilters: {
                ...this.state.turfFilters,
                noteTags: [...this.state.turfFilters.noteTags, res],
              },
            });
          }}
          onSelect={() => null}
          categories={this.props.categories.filter((d) => d.for_doors)}
        />
        <NoteTagList
          notes={this.state.turfFilters.noteTags}
          editable
          onRemoveNote={(data) => {
            this.setState({
              turfFilters: {
                ...this.state.turfFilters,
                noteTags: [
                  ...this.state.turfFilters.noteTags.filter(
                    (d) =>
                      !(d.category === data.category && d.note === data.note)
                  ),
                ],
              },
            });
          }}
        />
      </div>
    );

    return filters;
  }

  handleTurfTextFilter = (val) => {
    this.setState({
      turfFilterText: val,
    });
  };

  _filterTurfs = () => {
    let {
      turfs,
      turfStatusFilters,
      turfPassFilter,
      turfCodeFilter,
      intendedWalkStartDate,
      intendedWalkEndDate,
    } = this.state;

    if (this.state.turfFilters.keyword.length >= 2) {
      turfs = turfs.filter((d) =>
        d.name
          .toLowerCase()
          .includes(this.state.turfFilters.keyword.toLowerCase())
      );
    }

    turfStatusFilters.forEach((d) => {
      switch (d) {
        case "walked":
          turfs = turfs.filter(
            (t) => +_get(t, "agg_data.targets_attempted", 0) === 0
          );
          break;

        case "unwalked":
          turfs = turfs.filter(
            (t) => +_get(t, "agg_data.targets_attempted", 0) > 0
          );
          break;

        case "assigned":
          turfs = turfs.filter((t) => !t.assigned);

        default:
          return;
      }
    });

    if (turfPassFilter) {
      turfs = turfs.filter((d) => d.doorcanvasspass === turfPassFilter);
    }

    if (turfCodeFilter) {
      if (turfCodeFilter === "walkable") {
        turfs = turfs.filter((d) => {
          const matching_code =
            this.props.turfCodes.find((tc) => tc.id === d.code) || {};
          return !!matching_code.walkable;
        });
      } else if (turfCodeFilter === "not_walkable") {
        turfs = turfs.filter((d) => {
          const matching_code =
            this.props.turfCodes.find((tc) => tc.id === d.code) || {};
          return !matching_code.walkable;
        });
      } else if (!isNaN(+turfCodeFilter)) {
        turfs = turfs.filter((d) => d.code === turfCodeFilter);
      }
    }

    if (intendedWalkStartDate) {
      turfs = turfs.filter(
        (d) =>
          d.intended_walk_date &&
          Moment(d.intended_walk_date) >= Moment(intendedWalkStartDate)
      );
    }

    if (intendedWalkEndDate) {
      turfs = turfs.filter(
        (d) =>
          d.intended_walk_date &&
          Moment(d.intended_walk_date) <= Moment(intendedWalkEndDate)
      );
    }

    if (this.state.turfFilters.noteTags.length) {
      this.state.turfFilters.noteTags.forEach((noteTagFilter) => {
        turfs = filterItemsByNoteTag(turfs, noteTagFilter);
      });
    }

    return sortItems(
      turfs,
      this.state.turfFilters.sortBy,
      this.state.turfFilters.sortDsc
    );
  };

  _renderTurfFilterModal = () => {
    return (
      <BaseModal
        open={this.state.showTurfFilters}
        onClose={() => this.setState({ showTurfFilters: false })}
        center
      >
        <div className="build-crew-sheet-modal-wrapper">
          <div className={"modal-section"}>
            <h3>Filter Turfs</h3>
            {this._renderTurfFilterOptions()}
          </div>
          <div className={"modal-section"}>
            <div
              className={"button"}
              onClick={() => this.setState({ showTurfFilters: false })}
            >
              OK
            </div>
          </div>
        </div>
      </BaseModal>
    );
  };

  _renderScheduledCanvassers() {
    // start by getting an array of assigned assignedSites so that we can mark them
    let shift_id = this.state.shiftData[this.state.crewsheet_selected_shift_idx]
      ? this.state.shiftData[this.state.crewsheet_selected_shift_idx].id
      : 0;

    let assigned_canvassers = findCanvassersAssignedToShift(
      this.state.crews,
      shift_id
    );

    let canvasserValue =
      this.state.canvasserFilters.keywordPersist !== ""
        ? { value: this.state.canvasserFilters.keyword }
        : { test: 1 };

    const debouncedCanvasserSearch = debounce((query) => {
      const stateChanges = handleSearchChange(query);
      this.setState(
        {
          ...stateChanges[0],
          canvasserFilters: {
            ...this.state.canvasserFilters,
            keyword: stateChanges[1],
          },
        },
        () =>
          filterAndSortItems(this.props.canvassers, this.state.canvasserFilters)
      );
    }, 300);

    return (
      <>
        <CanvasserLegend
          open={this.state.showLegendModal}
          handleFilterUpdate={(statuses, assigned) => {
            this.setState({
              showLegendModal: false,
              displayedCanvasserStatuses: statuses,
              showAssignedCanvassers: assigned,
            });
          }}
        />
        <FilterDisplay
          filters={this.state.canvasserFilters}
          openFilterModal={() => this.setState({ showLegendModal: true })}
          handleSearchChange={(val) => {
            this.setState({
              canvasserFilters: {
                ...this.state.canvasserFilters,
                keywordPersist: "",
              },
            });
            debouncedCanvasserSearch(val);
          }}
          handleSortUpdate={(val) => {
            this.setState({
              canvasserFilters: {
                ...this.state.canvasserFilters,
                sortBy: val,
              },
            });
          }}
          handleSortDirectionChange={() =>
            this.setState({
              canvasserFilters: {
                ...this.state.canvasserFilters,
                sortDsc: !this.state.canvasserFilters.sortDsc,
              },
            })
          }
          value={canvasserValue}
        />

        <Droppable
          droppableId="displayedCanvassers"
          style={{ minHeight: "100px", minWidth: "10rem", width: "100%" }}
        >
          {(provided) => (
            <div
              ref={provided.innerRef}
              style={{ minHeight: "100px", minWidth: "10rem", width: "100%" }}
            >
              {filterAndSortItems(
                this.state.displayedCanvassers,
                this.state.canvasserFilters
              )
                .filter((d) =>
                  this.state.displayedCanvasserStatuses.includes(d.status)
                )
                .filter((d) =>
                  this.state.showAssignedCanvassers
                    ? true
                    : !assigned_canvassers.includes(d.id)
                )
                .map((item, index) => (
                  <DraggableShiftCanvasser
                    key={item.lastname + "-" + item.id}
                    index={index}
                    id={`canvasser-card-${item.id}`}
                    name={item.firstname + " " + item.lastname}
                    canvasser={item}
                    draggableId={`canvasser_${item.id}`}
                    color={this.colorClassMapping[item.status].color}
                    assigned={assigned_canvassers.includes(item.id)}
                  />
                ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </>
    );
  }

  _renderCrews = () => {
    let hasShifts = this.state.shiftData.length > 0;
    let dropMatMessage = hasShifts
      ? "Drag a crew over to start scheduling"
      : "Create a shift before adding routes.";

    let crews = cloneDeep(this.state.crews);
    let shift_id = this.state.shiftData[this.state.crewsheet_selected_shift_idx]
      ? this.state.shiftData[this.state.crewsheet_selected_shift_idx].id
      : 0;

    crews = crews.filter((crew) => crew.shift === shift_id);

    return (
      //expanding droppable to enter crews. then moved over to the state object
      <>
        <Droppable droppableId="crewsDrop">
          {(provided, snapshot) => (
            <>
              {!hasShifts && (
                <div
                  ref={provided.innerRef}
                  id="crew-drop-mat"
                  className={`crew-drop-mat`}
                >
                  <div className={`drop-text-wrapper warning`}>
                    <div className="drop-text-plus">x</div>
                    <div className="drop-text-sub">Create a shift first</div>
                  </div>
                  {provided.placeholder}
                </div>
              )}
              {hasShifts && (
                <div
                  ref={provided.innerRef}
                  id="crew-drop-mat"
                  className={`crew-drop-mat
                                    ${
                                      document.getElementById(
                                        "crew-drop-mat"
                                      ) &&
                                      document
                                        .getElementById("crew-drop-mat")
                                        .className.includes("active")
                                        ? "active"
                                        : ""
                                    }
                                    ${
                                      document.getElementById(
                                        "crew-drop-mat"
                                      ) &&
                                      document
                                        .getElementById("crew-drop-mat")
                                        .className.includes("inline")
                                        ? "inline"
                                        : ""
                                    }
                                    ${
                                      snapshot.isDraggingOver
                                        ? " primary-milligram light "
                                        : ""
                                    }\`}>
                                 
                                 `}
                >
                  <div className={`drop-text-wrapper`}>
                    {!crews.length && <div className="drop-text-plus">+</div>}
                    <div className="drop-text-sub">
                      {!!crews.length && "+ "}Create a new Crew
                    </div>
                  </div>
                  {provided.placeholder}
                </div>
              )}
            </>
          )}
        </Droppable>
        <div className={"crewlist"}>
          {crews.length ? (
            crews.map((item, index) => {
              return (
                <DraggableTurfAssignment
                  key={"crew-" + item.id}
                  index={index}
                  crew_id={item.id}
                  id={`crew-card-${item.id}`}
                  draggableId={`crew_${item.id}`}
                  data={item}
                  availableRoute={false}
                  draggable={true}
                  canvassers={this.props.canvassers}
                  getParentState={() => ({ ...this.state })} // do not change this to be a property, or any cahnge in parent state will force rerender of child
                  setParentState={(obj) => this.setState(obj)}
                  cardInDraggableContext={false} //if it's outside a draggable, then make element not dragganble. Otherwise will not render
                  onCanvasserSelect={(
                    canvasser_id,
                    routeseat_id,
                    crewcanvasser_id,
                    _delete
                  ) => {
                    this._handleCanvasserSelect(
                      item.id,
                      routeseat_id,
                      canvasser_id,
                      crewcanvasser_id,
                      _delete
                    );
                  }}
                  onDeleteCrew={() => this._handleDeleteCrew(item.id)}
                  onDeleteSeat={this._handleDeleteSeat}
                  onUnassignSeat={(crewcanvasser_id) =>
                    this._handleCanvasserSelect(crewcanvasser_id, null)
                  }
                  onCanvasserUpdate={this.setCrewsFromProps} //crews are fully maintained in redux. On update, just reset from redux.
                  handleAssignDropoffRequest={() =>
                    this.setState({ dropoff_modal_crew: item.id })
                  }
                />
              );
            })
          ) : (
            <div className={`empty-drop-mat`}>
              {!this._isLoading() && dropMatMessage}
            </div>
          )}
        </div>
      </>
    );
  };

  _handleCrewCanvasserChange = (crew_id, seat_data) => {
    let crews = cloneDeep(this.state.crews);
    let crew_index = crews.findIndex((d) => d.crew_id === +crew_id);
    if (crew_index < 0) {
      console.warn(
        "Tried to update a crew, but none found that matches id ",
        crew_id
      );
      return;
    }

    let seat_index = crews[crew_index].routeseats.findIndex(
      (d) => d.crewcanvasser_id === +seat_data.crewcanvasser_id
    );
    if (seat_index > -1) {
      crews[crew_index].routeseats[seat_index] = seat_data;
      this.setState({ crews });
    } else {
      console.warn(
        "Tried to update a crewcanvasser, but none found that matches id ",
        seat_data.crewcanvasser_id
      );
    }
  };

  createNewShift() {
    const data = {
      office: this.state.focusedOffice,
      start: this.state.shiftStart,
      end: this.state.shiftEnd,
      date: this.state.selectedDate,
    };

    let timeResult = this.buildStrictTimeString(data);

    if (!timeResult.strictEnd || !timeResult.strictStart) {
      this.setState({ invalidInput: true });
      return;
    }

    if (!this.checkValidRange(timeResult.strictStart, timeResult.strictEnd)) {
      this.setState({ invalidTime: true });
      return;
    }

    data.start = timeResult.strictStart;
    data.end = timeResult.strictEnd;

    createShift(data).then((data) => {
      if (data) {
        let shifts = this.state.shiftData.slice();
        shifts.push(data);
        this.props.setCrewsShiftData(data);
        this.setState({
          shiftData: shifts,
          shiftSuccess: true,
          showShiftModal: false,
          crewsheet_selected_shift_idx: shifts.length - 1,
        });
        setTimeout(() => this.setState({ shiftSuccess: false }), 6000);
      } else {
        this.setState({ shiftError: true, showShiftModal: false });
        setTimeout(() => this.setState({ shiftError: false }), 6000);
      }
    });
  }

  // getting a string time format to send to the db
  buildStrictTimeString(data) {
    let strictStart;
    let strictEnd;

    const cleanInput = (str) => str.replace(/\s/g, "").toLowerCase();
    const _start = cleanInput(data.start);
    const _end = cleanInput(data.end);

    // This regex has 3 capture groups to get each sensical piece of time to reconstruct.
    let startPieces = _start.match(
      /^0*(0?[1-9]|1[012]{1,2}):([0-5]\d)\d*([ap]m)$/
    );
    let endPieces = _end.match(/^0*(0?[1-9]|1[012]{1,2}):([0-5]\d)\d*([ap]m)$/);

    if (startPieces && endPieces) {
      strictStart = startPieces[1] + ":" + startPieces[2] + startPieces[3];
      strictEnd = endPieces[1] + ":" + endPieces[2] + endPieces[3];
    }

    return { strictStart, strictEnd };
  }

  checkValidRange(start, end) {
    return Moment(start, "LT").isBefore(Moment(end, "LT"));
  }

  setOrUnsetDropMapActive = (elem_id, set = true) => {
    document.getElementById(elem_id).className = "crew-drop-mat active";
  };

  _validateDateTime(data) {
    let valid = false;
    let validRange = false;
    let strictStart;
    let strictEnd;

    const cleanInput = (str) => str.replace(/\s/g, "").toLowerCase();

    if (!data.start || !data.end) {
      this.setState({ invalidInput: !valid });
      return valid;
    }

    const _start = cleanInput(data.start);
    const _end = cleanInput(data.end);

    valid = Moment(_start, "LT", true).isValid();
    valid = Moment(_end, "LT", true).isValid();
    validRange = Moment(_start, "LT").isBefore(Moment(data.end, "LT"));

    if (!valid) {
      this.setState({ invalidInput: !valid });
    } else if (!validRange) {
      // start was after end, not allowed.
      this.setState({ invalidTime: !validRange });
      valid = false;
    } else {
      // moment is lax with time values, so only capture and set the exact digits we want if it's valid.
      // ie: 009:1811am is a valid time to moment despite the LT format above. Don't submit that shift
      // This regex has 3 capture groups to get each sensical piece of time to reconstruct w/o the extra things moment allows.
      let startPieces = _start.match(
        /^0*(0?[1-9]|1[012]{1,2}):([0-5]\d)\d*([ap]m)$/
      );
      let endPieces = _end.match(
        /^0*(0?[1-9]|1[012]{1,2}):([0-5]\d)\d*([ap]m)$/
      );

      if (startPieces && endPieces) {
        strictStart = startPieces[1] + ":" + startPieces[2] + startPieces[3];
        strictEnd = endPieces[1] + ":" + endPieces[2] + endPieces[3];
      } else {
        // in the event the user has done something really wild and it got past moment, but failed the regex
        // send it back the user to fix.
        this.setState({ invalidInput: true });
        valid = false;
      }
    }

    return { valid, strictStart, strictEnd };
  }

  _handleTimeChange(key, val) {
    this.setState({
      [key]: val.trim(),
      invalidInput: false,
      invalidTime: false,
    });
  }

  _renderCreateShiftModal() {
    return (
      <BaseModal
        className=""
        open={this.state.showShiftModal}
        onClose={() => this.setState({ showShiftModal: false })}
        center
      >
        <div className="container">
          <i
            className="fas fa-pull-right fa-times"
            style={{ cursor: "pointer" }}
            onClick={() => this.setState({ showShiftModal: false })}
          />
          <h4 className={"primary-text"}>
            Create A Shift{" "}
            <span style={{ fontSize: ".7em" }}>
              ({this.state.selectedDate})
            </span>
          </h4>
          <span className={"secondary-text"}>Start Time </span>
          <span style={{ fontSize: ".7em" }}>(ie: 10:30am)</span>
          <Input
            // title={'Start Time'}
            name="start"
            placeholder={"10:30am"}
            onChange={this._handleTimeChange.bind(this, "shiftStart")}
          />
          <span className={"secondary-text"}>End Time </span>
          <span style={{ fontSize: ".7em" }}>(ie: 5:00pm)</span>
          <Input
            // title={'End Time'}
            type="text"
            name="end"
            placeholder={"5:00pm"}
            onChange={this._handleTimeChange.bind(this, "shiftEnd")}
          />
          {this.state.invalidInput ? (
            <div
              style={{
                fontSize: ".8em",
                fontWeight: "bold",
                marginBottom: "1em",
              }}
            >
              <div className="danger-text"> Invalid Date / Time</div>
              Please make sure your times are correct. <br />
              See time format hints for help.
            </div>
          ) : null}
          {this.state.invalidTime ? (
            <div
              style={{
                fontSize: ".8em",
                fontWeight: "bold",
                marginBottom: "1em",
              }}
            >
              <div className="danger-text"> End must be after start time.</div>
            </div>
          ) : null}
          <button
            disabled={!this.state.shiftStart || !this.state.shiftEnd}
            className={`button button-outline text-primary`}
            onClick={this.createNewShift.bind(this)}
          >
            Submit
          </button>
        </div>
      </BaseModal>
    );
  }

  _changeDate = (add = true) => {
    this.setState({ crews: [] });

    let { selectedDate } = this.state;
    let mom = new Moment(selectedDate, "YYYY-MM-DD");

    if (add) {
      mom = mom.add(1, "days");
    } else {
      mom = mom.subtract(1, "days");
    }

    let formatted_mom = mom.format("YYYY-MM-DD");
    this.setState(
      { selectedDate: formatted_mom, crewsheet_selected_shift_idx: 0 },
      this._setDateChangeTimeout
    );
  };

  _setDateChangeTimeout() {
    clearTimeout(this.dateChangeTimeout);
    this.dateChangeTimeout = setTimeout(() => this.getDataOnLoad(), 750);
  }

  _isLoading = () => {
    return (
      (this.props.siteroutes_loading ||
        this.props.canvassers_loading ||
        this.state.loading) &&
      !this._isError()
    );
  };

  _isError = () => {
    return (
      this.props.canvassers_error ||
      this.props.siteroutes_error ||
      this.state.shiftError ||
      this.state.pageError
    );
  };

  //todo this is broken
  _addOrRemoveCrewFromCrewSheet = (crew_id) => {
    const inSheet = this.state.crewsheet_crews.includes(crew_id);
    if (inSheet) {
      let crewsheet_crews = this.state.crewsheet_crews.filter(
        (d) => d !== +crew_id
      );
      this.setState(
        { crewsheet_crews },
        this.props.setCrewsForRouting.bind(
          this,
          this.state.crews.filter((d) => crewsheet_crews.includes(d.crew_id))
        )
      );
    } else {
      let crewsheet_crews = [...this.state.crewsheet_crews, crew_id];
      this.setState(
        { crewsheet_crews },
        this.props.setCrewsForRouting.bind(
          this,
          this.state.crews.filter((d) => crewsheet_crews.includes(d.crew_id))
        )
      );
    }
  };

  _buildCrewSheetModal = () => {
    let shift_id = this.state.shiftData[this.state.crewsheet_selected_shift_idx]
      ? this.state.shiftData[this.state.crewsheet_selected_shift_idx].id
      : 0;
    this.props.setCrewsForRouting(
      this.state.crews.filter(
        (d) => this.state.crewsheet_crews.includes(d.id) && d.shift === shift_id
      )
    );
    return (
      <BaseModal
        open={this.state.crewsheet_modal_show}
        onClose={() => this.setState({ crewsheet_modal_show: false })}
        center
      >
        <div className="build-crew-sheet-modal-wrapper">
          <div className={"modal-section"}>
            <h3>Build Crew Sheet</h3>
          </div>
          <div className={"modal-section"}>
            <Input
              type={"switch-left"}
              checked={!this.props.sessionVars.hideDrivingDirections}
              defaultChecked={true}
              onChange={() => {
                this.props.setSessionVar(
                  "hideDrivingDirections",
                  !this.props.sessionVars.hideDrivingDirections
                );
              }}
              title={"Print Driving Directions"}
            />
          </div>
          <div className={"modal-section"}>
            <div className={"flx"}>
              <h5 className={"primary-text"} style={{ marginBottom: 0 }}>
                Filter Crews
              </h5>
              <div className={"ml-6"}>
                <div
                  className="button button-clear "
                  onClick={() => {
                    this.props.setSessionVar(
                      "excludedCrews",
                      this.props.crews.map((d) => d.id)
                    );
                  }}
                >
                  Deselect All
                </div>
                <div
                  className="button button-clear px-0 ml-3"
                  // style={{ marginLeft: "1em" }}
                  onClick={() => {
                    this.props.setSessionVar("excludedCrews", []);
                  }}
                >
                  Select All
                </div>
              </div>
            </div>
            {this.state.crews.map((d) => {
              if (
                this.state.shiftData.length > 0 &&
                d.shift ===
                  this.state.shiftData[this.state.crewsheet_selected_shift_idx]
                    .id
              ) {
                return (
                  <Input
                    type={"switch-left"}
                    key={d.id}
                    checked={
                      !this.props.sessionVars.excludedCrews?.includes(d.id)
                    }
                    defaultChecked={true}
                    onChange={() => {
                      let { excludedCrews } = this.props.sessionVars;
                      if (
                        this.props.sessionVars.excludedCrews?.includes(d.id)
                      ) {
                        excludedCrews = excludedCrews.filter((c) => c !== d.id);
                      } else {
                        excludedCrews = excludedCrews
                          ? [...excludedCrews, d.id]
                          : [d.id];
                      }
                      this.props.setSessionVar("excludedCrews", excludedCrews);
                    }}
                    title={d.id}
                    style={{ padding: "5px", marginBottom: "5px" }}
                    classNames={"no-padding no-margin"}
                  />
                );
              }
              return <></>;
            })}
          </div>

          <div className={"modal-section"}>
            <div
              className={`button ${
                this.props.sessionVars?.excludedCrews?.length ===
                this.props.crews.filter(
                  (crew) => crew.canvass === +this.focusedDoorCanvass
                ).length
                  ? "disabled"
                  : ""
              }`}
              onClick={this._handlePrintCrewSheetsRequest}
            >
              Print Crew Sheets
            </div>
            <div
              className={"button danger"}
              style={{ marginLeft: "1em" }}
              onClick={() => this.setState({ crewsheet_modal_show: false })}
            >
              Cancel
            </div>
          </div>
        </div>
      </BaseModal>
    );
  };

  _handleOfficeChange = (val) => {
    this.setState(
      {
        focusedOffice: +val,
      },
      this.getAndParseSchedule
    );
  };

  _handleShiftChange(val) {
    const shiftDataIndex = this.state.shiftData.findIndex((d) => d.id === +val);
    let shiftData = {};
    if (shiftDataIndex > -1) shiftData = this.state.shiftData[shiftDataIndex];
    this.props.setCrewsShiftData(shiftData);
    this.setState({
      shiftSuccess: false,
      showShiftModal: false,
      crewsheet_selected_shift_idx: shiftDataIndex,
      displayedCanvassers: [
        ...this.state.displayedCanvassers.map((d) =>
          this.setShiftCanvasserStatus(d)
        ),
      ],
    });
    // this.getDataOnLoad();
  }

  _loadCrewSheetModal = () => {
    this.setState({
      crewsheet_modal_show: true,
      crewsheet_crews: this.state.crews.map((d) => d.crew_id),
    });
  };

  _handlePrintCrewSheetsRequest = () => {
    const searchParams = new URLSearchParams();
    let crewsArr = [];
    this.props.crews.forEach((crew) => {
      if (
        +crew.canvass === +this.focusedDoorCanvass &&
        !this.props.sessionVars.excludedCrews?.includes(crew.id)
      ) {
        crewsArr = [...crewsArr, crew.id];
      }
    });
    searchParams.append("crews", crewsArr);

    window.open(
      `/door-crewsheet/${this.props.shift_data.id}/?${searchParams.toString()}`,
      "_blank"
    );
  };

  render() {
    let shiftCrews = this.state.crews;
    let shift_id = 0;

    if (this.state.shiftData[this.state.crewsheet_selected_shift_idx]) {
      shift_id = this.state.shiftData[this.state.crewsheet_selected_shift_idx]
        .id
        ? this.state.shiftData[this.state.crewsheet_selected_shift_idx].id
        : 0;
      shiftCrews = this.state.crews.filter((crew) => crew.shift === shift_id);
    }

    return (
      <NavBaseTemplate disabled={true} >
        <DragDropContext
          // onBeforeDragStart={this.onDragStart}
          onDragEnd={this.onDragEnd}
        >
          <div className="flx space_between align_base schedule-title-flex">
            <h3 className="primary-text">Schedule Door Crews</h3>
            <div>
              {/* <a href={`/door-crewsheet/${this.props.shift_data.id}/`} target="_blank"> */}
              <button
                disabled={
                  !shiftCrews.length ||
                  this.state.loading ||
                  !this.state.shiftData[this.state.crewsheet_selected_shift_idx]
                }
                className={`button button-outline text-primary ${
                  shiftCrews.length &&
                  !this.state.loading &&
                  this.state.shiftData[this.state.crewsheet_selected_shift_idx]
                    ? ""
                    : "disabled"
                }`}
                onClick={this._loadCrewSheetModal}
              >
                Build Crew Sheets
              </button>
              {/* </a> */}
              <button
                style={{
                  marginLeft: "1em",
                  color: "#2F96B4",
                  borderColor: "#2F96B4",
                }}
                className={`button button-outline text-primary`}
                onClick={() => this.setState({ showShiftModal: true })}
              >
                Create A Shift
              </button>
            </div>
          </div>

          <div className="row">
            <div className="column column-80">
              <div className="flx space_between schedule-options-flex">
                <div className="flx justify-start date-select-line">
                  <i
                    className={`fas fa-arrow-left fa-pull-left
                                                    ${
                                                      new Moment(
                                                        Date.now()
                                                      ).format("YYYY-MM-DD") ===
                                                      this.state.selectedDate
                                                        ? ""
                                                        : null
                                                    }
                                                `}
                    onClick={() => this._changeDate(false)}
                    title="Previous Day"
                  />
                  <h5>{this.state.selectedDate}</h5>
                  <i
                    className="fas fa-arrow-right fa-pull-right"
                    title="Next Day"
                    onClick={() => this._changeDate(true)}
                  />
                </div>
                <div className="flx justify-end">
                  <div className="override-milligram scheduler-drop-down">
                    {this.state.shiftData.length > 0 ? (
                      <>
                        <div className="mr-3">Shift</div>
                        <Input
                          type={"select"}
                          placeholder={"Select a shift"}
                          value={this.props.shift_data.id}
                          options={
                            this.state.shiftData
                              ? this.state.shiftData.map((d, i) => ({
                                  value: d.id,
                                  display:
                                    new Moment(
                                      `${d.shift_date} ${d.start_time}`
                                    ).format("LT") +
                                    " - " +
                                    new Moment(
                                      `${d.shift_date} ${d.end_time}`
                                    ).format("LT"),
                                }))
                              : null
                          }
                          onChange={(val) => this._handleShiftChange(val)}
                        />
                      </>
                    ) : null}
                  </div>

                  <div className="override-milligram scheduler-drop-down">
                    <div className="mr-3">Office</div>
                    <Input
                      type={"select"}
                      value={this.state.focusedOffice}
                      options={
                        this.props.offices
                          ? this.props.offices.map((d, i) => ({
                              value: d.id,
                              display: d.name,
                            }))
                          : null
                      }
                      onChange={(val) => this._handleOfficeChange(val)}
                    />
                  </div>
                </div>
              </div>
              <div className="card wox-card crews-card">
                <div className="">{this._renderCrews()}</div>
              </div>
            </div>
            <div className="column column-20">
              <Tabs
                tabs={[
                  {
                    display: (
                      <i
                        style={{ padding: "0 1em" }}
                        className="fas fa-route"
                      />
                    ),
                    onClick: () => this.setState({ sideBarSelection: "turfs" }),
                    active: this.state.sideBarSelection === "turfs",
                  },
                  {
                    display: (
                      <i
                        style={{ padding: "0 1em" }}
                        className="fas fa-user-clock"
                      />
                    ),
                    onClick: () =>
                      this.setState({ sideBarSelection: "canvassers" }),
                    active: this.state.sideBarSelection === "canvassers",
                  },
                ]}
                style={{
                  marginBottom: ".8em",
                  justifyContent: "center",
                }}
              />

              <div className="card wox-card crews-card assigned-canvasser-counter primary-text">
                <div>
                  {
                    findCanvassersAssignedToShift(this.state.crews, shift_id)
                      .length
                  }{" "}
                  Canvassers assigned
                </div>

                {Moment(this.state.selectedDate).isSame(Date.now(), "day") && (
                  <div>
                    {
                      findCheckedInCanvassers(this.state.displayedCanvassers)
                        .length
                    }{" "}
                    Canvassers checked in
                  </div>
                )}
              </div>

              <div className="card wox-card crews-card">
                <div className="sidebar-wrapper">
                  {this.state.sideBarSelection == "turfs"
                    ? this._renderSidebarTurfs()
                    : this._renderScheduledCanvassers()}
                </div>
              </div>
            </div>
          </div>
        </DragDropContext>

        {this.state.crewsheet_modal_show && this._buildCrewSheetModal()}
        {this._renderCreateShiftModal()}
        {this._renderTurfFilterModal()}

        <FeedbackTile
          text={
            <div>
              <i
                className=" fas fa-asterisk fa-spin"
                style={{ marginRight: "15px" }}
              />
              Loading...
            </div>
          }
          visible={this._isLoading()}
        />
        <FeedbackTile
          text={
            <div>
              <i className="fas fa-check" style={{ marginRight: "15px" }}></i>
              Shift Created
            </div>
          }
          visible={this.state.shiftSuccess}
        />

        {/*use feedback tile icon, text, state*/}
        <FeedbackTile
          text={
            <div>
              <i
                className="fas fa-exclamation-triangle"
                style={{ marginRight: "15px" }}
              />
              {this.state.errMsg}
            </div>
          }
          visible={this._isError()}
          classNames="danger"
        />

        {this.state.dropoff_modal_crew && (
          <TurfDropoffMapModal
            shift={this.props.shift_data.id}
            crew={this.state.dropoff_modal_crew}
            crews={this.props.crews}
            onClose={() => this.setState({ dropoff_modal_crew: null })}
          />
        )}
        <TurfNoteModal
          handleNoteCreateOrDestroy={this.handleTurfNoteCreateOrDestroy}
          showCloseIcon
          center
        />
      </NavBaseTemplate>
    );
  }
} //end class

const mapStateToProps = (state) => ({
  focusedCampaign: state.session.focusedCampaign,
  doorCanvasses: state.doorCanvasses.data,
  doorCanvassPasses: state.doorCanvassPasses.data,
  crews: state.doorCrews.data,
  crews_loading: state.doorCrews.loading,
  crews_error: state.doorCrews.error,
  canvassers: state.canvassers.data,
  canvassers_loading: state.canvassers.loading,
  canvassers_error: state.canvassers.error,
  is_admin: state.permissions.is_admin,
  shift_data: state.crews.shift_data,
  turfCodes: state.turfCodes.data,
  offices: state.offices.data,
  sessionVars: state.session.vars,
  categories: state.siteNotes.categories,
});

const mapDispatchToProps = (dispatch) => ({
  setCrewsForRouting: (crews) => dispatch(setCrews(crews)),
  setCrewsShiftData: (data) => dispatch(setCrewsShiftData(data)),
  getTurfCodes: () => dispatch(doorCalls.getTurfCodes()),
  setTurfNoteModalTurf: (turf) =>
    dispatch(setSessionVar("turfNoteModalTurf", turf)),
  setSessionVar: (key, val) => dispatch(setSessionVar(key, val)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(DoorSchedule));
